import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { STICKY_ABOVE_APP_FOOTER } from '@/components/footerLayout';
import { Ban, Loader2, RotateCcw, Save, X } from 'lucide-react';

/** Ensure label + icon stay white on destructive (some themes override button text). */
const resetDestructiveButtonClass =
  '!text-white hover:!text-white [&_svg]:!text-white';

interface PolicySettingsActionsBaseProps {
  hasCustomizedSettings: boolean;
  policyName: string;
  isDirty: boolean;
  isSubmitting: boolean;
  readOnly?: boolean;
  /** When false, Save stays disabled (e.g. allow/block validation). Default true. */
  isValid?: boolean;
  onReset: () => void;
}

interface ResetOnlyPolicySettingsActionsProps extends PolicySettingsActionsBaseProps {
  /**
   * Allow/block auto-persist: no Save/Cancel — only show destructive reset when customized.
   */
  resetOnly: true;
  onSave?: never;
  onCancel?: never;
}

interface StandardPolicySettingsActionsProps extends PolicySettingsActionsBaseProps {
  resetOnly?: false;
  onSave: () => void;
  onCancel: () => void;
}

type PolicySettingsActionsProps =
  | ResetOnlyPolicySettingsActionsProps
  | StandardPolicySettingsActionsProps;

export function PolicySettingsActions(
  props: PolicySettingsActionsProps
): ReactElement | null {
  const {
    hasCustomizedSettings,
    policyName,
    isDirty,
    isSubmitting,
    readOnly = false,
    isValid = true,
    resetOnly = false,
    onReset,
  } = props;

  const renderResetAction = (): ReactElement | null => {
    if (!hasCustomizedSettings) {
      return null;
    }
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="destructive"
            type="button"
            disabled={isSubmitting}
            className={resetDestructiveButtonClass}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset to Defaults
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Policy Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset all settings for &quot;{policyName}&quot; back to
              their default values. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onReset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Reset Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  // Hide all actions when in read-only mode
  if (readOnly) {
    return null;
  }

  if (resetOnly) {
    return (
      <div className="flex items-center justify-end pt-4 border-t">
        {renderResetAction()}
      </div>
    );
  }
  const { onSave, onCancel } = props;

  const saveDisabled = !isDirty || isSubmitting || !isValid;
  const blockedByInvalid = !isValid;

  const saveButton = (
    <Button
      size="sm"
      type="button"
      onClick={onSave}
      disabled={saveDisabled}
      aria-label={
        blockedByInvalid
          ? 'Save unavailable — fix invalid fields before saving'
          : 'Save'
      }
    >
      {isSubmitting ? (
        <Loader2 className="h-4 w-4 mr-1 shrink-0 animate-spin" aria-hidden />
      ) : blockedByInvalid ? (
        <Ban className="h-4 w-4 mr-1 shrink-0" aria-hidden />
      ) : (
        <Save className="h-4 w-4 mr-1 shrink-0" aria-hidden />
      )}
      {isSubmitting ? 'Saving...' : 'Save'}
    </Button>
  );

  // KIR-4902: with unsaved changes the bar sticks to the viewport bottom, flush
  // on the app footer, so Save is reachable without scrolling.
  const showUnsavedBar = isDirty;

  return (
    <div
      data-testid="policy-settings-actions"
      className={cn(
        'flex items-center justify-between gap-2 pt-4',
        showUnsavedBar && STICKY_ABOVE_APP_FOOTER,
        showUnsavedBar
          ? // Content fades into the bar instead of meeting a hard edge.
            'z-10 bg-card pb-4 before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-8 before:bg-linear-to-t before:from-card before:to-transparent'
          : 'border-t'
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {showUnsavedBar && (
          <span
            role="status"
            className="mr-2 text-sm font-medium text-muted-foreground"
          >
            {/* Named: with several cards open, a stuck bar's card header may
                be scrolled away. */}
            Unsaved changes to {policyName} — save to apply them
          </span>
        )}
        {blockedByInvalid ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-not-allowed">
                {saveButton}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              Fix invalid fields before saving.
            </TooltipContent>
          </Tooltip>
        ) : (
          saveButton
        )}
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={onCancel}
          disabled={!isDirty || isSubmitting}
        >
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
      </div>

      {renderResetAction()}
    </div>
  );
}
